import { API } from '@/utils/API';
import { sanitizePostLoginRedirect } from '@/utils/postLoginRedirect';
import {
  MFA_VERIFICATION_CHALLENGES,
  type LoginResponseBody,
  type MfaVerificationChallenge,
} from './loginFormTypes';

export const isMfaVerificationChallenge = (
  challengeName: string | undefined
): challengeName is MfaVerificationChallenge =>
  MFA_VERIFICATION_CHALLENGES.includes(
    challengeName as MfaVerificationChallenge
  );

export interface LoginRequestParams {
  email: string;
  password: string;
  challengeCode: string;
}

export const submitLogin = (params: LoginRequestParams) =>
  API.fetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email: params.email,
      password: params.password,
      challenge_code: params.challengeCode,
    }),
  });

// "I lost my authenticator" — identity comes from the server-side challenge
// session established by the password step, so no body is sent.
export const submitMfaResetRequest = () =>
  API.fetch('/auth/mfa_reset_requests', { method: 'POST' });

export const redirectAfterLogin = async () => {
  const meRes = await API.authenticated.fetch('/users/me');
  let canAccessAdmin = false;
  if (meRes.ok) {
    const me = await meRes.json();
    canAccessAdmin = Boolean(me.can_access_admin);
  }

  const params = new URLSearchParams(window.location.search);
  const target = sanitizePostLoginRedirect(params.get('redirect'), {
    canAccessAdmin,
  });
  window.location.href = target;
};

export type LoginOutcome =
  | { type: 'error'; message: string }
  | { type: 'challenge'; challengeType: MfaVerificationChallenge }
  | { type: 'success' };

export const interpretLoginResponse = (
  _response: Response,
  data: LoginResponseBody
): LoginOutcome => {
  if (data.error) {
    return { type: 'error', message: data.error };
  }

  if (data.challenge) {
    const { challenge_name: challengeName } = data.challenge;

    if (!isMfaVerificationChallenge(challengeName)) {
      console.error(
        'Unsupported login challenge:',
        challengeName,
        data.challenge
      );
      return {
        type: 'error',
        message:
          'Your account needs additional setup before you can sign in. Please try again or contact support.',
      };
    }

    return { type: 'challenge', challengeType: challengeName };
  }

  return { type: 'success' };
};
